'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';

export default function PricingSection() {
  const { t } = useLanguage();

  const scrollToContact = (planName: string) => {
    const contactEl = document.getElementById('contacte');
    if (contactEl) {
      contactEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="preus"
      className="py-24 px-6 bg-[#07070b] relative overflow-hidden border-t border-white/5"
      aria-label="Preus i plans"
    >
      <div className="max-w-7xl mx-auto space-y-16 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-gold/30 bg-gold/5 text-gold text-[11px] font-bold uppercase tracking-widest">
            {t.pricing.badge}
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white tracking-tight uppercase">
            {t.pricing.title}
          </h2>
          <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
            {t.pricing.sub}
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Plan 1: Onix Presència */}
          <div className="rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-[#111119] to-[#09090e] border border-gold/40 flex flex-col justify-between space-y-8 relative overflow-hidden">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-gold bg-gold/10 px-3 py-1 rounded-full border border-gold/20">
                  {t.pricing.plan1.tag}
                </span>
                <span className="text-xs text-emerald-400 font-bold bg-emerald-500/10 px-2.5 py-0.5 rounded-full">
                  {t.pricing.plan1.subTag}
                </span>
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-white">
                  {t.pricing.plan1.name}
                </h3>
                <div className="flex items-baseline gap-2 pt-2">
                  <span className="text-4xl sm:text-5xl font-black text-gold">{t.pricing.plan1.price}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t.pricing.plan1.period}
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10 text-xs sm:text-sm text-slate-300">
                <p className="text-xs font-bold uppercase text-white">{t.pricing.whatsIncluded}</p>
                <ul className="space-y-2.5">
                  {t.pricing.plan1.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <span className="text-gold font-bold">✓</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                  {t.pricing.plan1.optionalText && (
                    <li className="flex items-center gap-2.5 text-slate-400 text-xs italic">
                      <span className="text-slate-500">○</span>
                      <span>{t.pricing.plan1.optionalText}</span>
                    </li>
                  )}
                </ul>
              </div>
            </div>

            <button
              onClick={() => scrollToContact(t.pricing.plan1.name)}
              className="w-full py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-black bg-gradient-to-r from-gold via-gold-light to-gold-dark hover:brightness-110 shadow-lg shadow-gold/20 transition-all text-center"
            >
              {t.pricing.plan1.cta}
            </button>
          </div>

          {/* Plan 2: Onix Sistema */}
          <div className="rounded-3xl p-8 sm:p-10 bg-gradient-to-b from-[#161224] to-[#0a0812] border border-magenta-brand/40 shadow-xl shadow-purple-brand/10 flex flex-col justify-between space-y-8 relative overflow-hidden">
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black uppercase tracking-widest text-magenta-brand bg-magenta-brand/10 px-3 py-1 rounded-full border border-magenta-brand/30">
                  {t.pricing.plan2.tag}
                </span>
                <span className="text-xs text-purple-300 font-bold bg-purple-brand/30 px-2.5 py-0.5 rounded-full border border-purple-brand/40">
                  {t.pricing.plan2.subTag}
                </span>
              </div>

              <div>
                <h3 className="text-2xl sm:text-3xl font-black text-white">
                  {t.pricing.plan2.name}
                </h3>
                <div className="flex items-baseline gap-2 pt-2">
                  <span className="text-2xl sm:text-3xl font-black text-magenta-brand">{t.pricing.plan2.price}</span>
                </div>
                <p className="text-xs text-slate-400 pt-1">
                  {t.pricing.plan2.period}
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-white/10 text-xs sm:text-sm text-slate-300">
                <p className="text-xs font-bold uppercase text-white">{t.pricing.whatsIncluded}</p>
                <ul className="space-y-2.5">
                  {t.pricing.plan2.features.map((feat, i) => (
                    <li key={i} className="flex items-center gap-2.5">
                      <span className="text-magenta-brand font-bold">✓</span>
                      <span>{feat}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <button
              onClick={() => scrollToContact(t.pricing.plan2.name)}
              className="w-full py-4 rounded-xl text-xs sm:text-sm font-black uppercase tracking-wider text-white bg-gradient-to-r from-purple-brand via-magenta-brand to-purple-brand hover:brightness-110 shadow-lg shadow-purple-brand/30 transition-all text-center"
            >
              {t.pricing.plan2.cta}
            </button>
          </div>
        </div>

        {/* Note on Maintenance and SEO */}
        <div className="max-w-3xl mx-auto p-6 rounded-2xl bg-white/5 border border-white/10 text-xs text-slate-300 space-y-2 text-center">
          <p className="font-bold text-white uppercase tracking-wider">
            {t.pricing.noteTitle}
          </p>
          <p>
            {t.pricing.noteText}
          </p>
        </div>
      </div>
    </section>
  );
}
